import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, Eye, Edit, Trash2, Download } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface DocumentManagerProps {
  documents: string[];
  onDocumentDeleted?: (url: string) => void;
  onDocumentRenamed?: (oldUrl: string, newUrl: string) => void;
  viewMode?: boolean;
}

export const DocumentManager = ({ documents = [], onDocumentDeleted, onDocumentRenamed, viewMode = false }: DocumentManagerProps) => {
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [editingDocument, setEditingDocument] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  const getDocumentName = (url: string) => {
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  const isImage = (url: string) => {
    return url.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/);
  };

  const handleDelete = async (url: string) => {
    setIsDeleting(true);
    try {
      // Extract the file path from the URL
      const urlParts = url.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const bucketPath = `candidate-documents/${fileName}`;
      
      const { error } = await supabase.storage
        .from('candidate-documents')
        .remove([bucketPath]);
      
      if (error) {
        throw error;
      }
      
      if (onDocumentDeleted) {
        onDocumentDeleted(url);
      }
      toast({
        title: "Επιτυχία",
        description: "Το έγγραφο διαγράφηκε επιτυχώς",
      });
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η διαγραφή του εγγράφου",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRename = async (url: string) => {
    if (!newName.trim()) return;
    
    try {
      // For now, we'll just update the local state
      // In a real implementation, you might want to rename the file in storage
      // or update a database record with the new name
      
      if (onDocumentRenamed) {
        onDocumentRenamed(url, newName);
      }
      
      setEditingDocument(null);
      setNewName('');
      
      toast({
        title: "Επιτυχία",
        description: "Το έγγραφο μετονομάστηκε επιτυχώς",
      });
    } catch (error) {
      console.error('Error renaming document:', error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η μετονομασία του εγγράφου",
        variant: "destructive",
      });
    }
  };

  const handleDownload = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = getDocumentName(url);
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (documents.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        Δεν υπάρχουν έγγραφα
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Αποθηκευμένα Έγγραφα</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documents.map((url, index) => (
          <Card key={url} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="space-y-3">
                {/* Document Preview */}
                <div className="aspect-video bg-muted rounded-md overflow-hidden flex items-center justify-center">
                  {isImage(url) ? (
                    <img 
                      src={url} 
                      alt={`Document ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FileText className="h-12 w-12 text-muted-foreground" />
                  )}
                </div>
                
                {/* Document Name */}
                <div className="space-y-1">
                  {editingDocument === url ? (
                    <div className="space-y-2">
                      <Input
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Νέο όνομα"
                        className="text-sm"
                      />
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRename(url)}
                        >
                          Αποθήκευση
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingDocument(null);
                            setNewName('');
                          }}
                        >
                          Ακύρωση
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm font-medium truncate">
                      Έγγραφο {index + 1}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground truncate">
                    {getDocumentName(url)}
                  </p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex space-x-1">
                  {/* Detail View */}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => setSelectedDocument(url)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Προβολή
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                      <DialogHeader>
                        <DialogTitle>Προβολή Εγγράφου</DialogTitle>
                      </DialogHeader>
                      <div className="mt-4 overflow-y-auto flex-1">
                        {selectedDocument && isImage(selectedDocument) ? (
                          <img 
                            src={selectedDocument} 
                            alt="Document preview"
                            className="w-full max-h-[70vh] object-contain rounded-md mx-auto block"
                          />
                        ) : (
                          <div className="text-center py-8">
                            <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                            <p className="text-muted-foreground">
                              Προεπισκόπηση μη διαθέσιμη για αυτό το αρχείο
                            </p>
                            <Button
                              onClick={() => selectedDocument && handleDownload(selectedDocument)}
                              className="mt-4"
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Κατέβασμα
                            </Button>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Edit */}
                  {!viewMode && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingDocument(url);
                        setNewName(getDocumentName(url));
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  
                  {/* Delete */}
                  {!viewMode && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Διαγραφή Εγγράφου</AlertDialogTitle>
                          <AlertDialogDescription>
                            Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτό το έγγραφο; 
                            Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(url)}
                            disabled={isDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {isDeleting ? "Διαγραφή..." : "Διαγραφή"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};